/**
 * ManagerDashboard - Tier 2 dashboard organism for admin / manager /
 * company_admin roles.
 *
 * Mirrors SuperAdminDashboard's shape minus the platform-wide
 * tasks-by-user table, with revenue scoped to "the money I (or my
 * team / my company) have made". The user toggles between three
 * scopes via the scope filter chips:
 *   - Me      : records I personally initiated or am assigned to
 *   - Company : records anywhere in the companies I belong to
 *   - All     : same as Company (server caps non-super to 'company')
 *
 * Owns no fetch logic — the parent passes metrics + filters and
 * receives change events.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { MetricCard } from '../../molecules';
import { DashboardPeriodPicker } from '../DashboardPeriodPicker';
import { InspectionRatingsPanel } from '../InspectionRatingsPanel';
import { StatusBreakdown } from '../SuperAdminDashboard';
import { formatCurrencyCompact } from '../../../utils/formatCurrency';
import type { DashboardFilters, DashboardMetrics } from '../../../services/dashboardService';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

export interface ManagerDashboardProps {
    metrics: DashboardMetrics;
    filters: DashboardFilters;
    onFiltersChange: (next: DashboardFilters) => void;
    /** Optional outer className merged onto the root element. */
    className?: string;
}

/* ============================================
 * COMPONENT
 * ============================================ */

const SCOPE_OPTIONS: Array<{ key: 'me' | 'company'; label: string }> = [
    { key: 'me', label: 'My work' },
    { key: 'company', label: 'My company' },
];

export const ManagerDashboard: React.FC<ManagerDashboardProps> = ({
    metrics,
    filters,
    onFiltersChange,
    className,
}) => {
    const { counts, revenue, comparison } = metrics;
    const navigate = useNavigate();
    /* Default scope is 'company' on the server side; reflect that in
     * the active chip when the filter has no explicit scope. */
    const activeScope = filters.scope ?? 'company';

    /* Rating-tile click-through — same deep links the SuperAdminDashboard
     * uses; see the comment there for the count-parity reasoning. */
    const handleSelectRating = (rating: string) => navigate(`/inspections?rating=${rating}`);
    const handleSelectCritical = () => navigate('/inspections?rating=critical&status=pending,in_progress,observations_complete,corrections_in_progress');

    const handleScope = (scope: 'me' | 'company') => {
        onFiltersChange({ ...filters, scope });
    };

    return (
        <div className={['manager-dashboard', 'space-y-24px', className ?? ''].join(' ')} data-ref="organism-manager-dashboard">
            {/* Top row — period picker + scope chips + comparison label. */}
            <div className="manager-dashboard__header flex flex-wrap items-center justify-between gap-12px">
                <div className="flex flex-wrap items-end gap-16px">
                    <DashboardPeriodPicker value={filters} onChange={onFiltersChange} />
                    <div className="flex items-end gap-8px">
                        {SCOPE_OPTIONS.map((opt) => (
                            <button
                                key={opt.key}
                                type="button"
                                onClick={() => handleScope(opt.key)}
                                className={[
                                    'px-12px py-8px text-13px font-sans font-medium border rounded-md transition-colors min-h-44px desktop:min-h-36px',
                                    activeScope === opt.key
                                        ? 'bg-brand-accent text-white border-brand-accent'
                                        : 'bg-white text-brand-text border-brand-border hover:border-brand-accent/40',
                                ].join(' ')}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>
                </div>
                {comparison && (
                    <span className="text-12px font-sans text-brand-text-muted">
                        vs <strong>{comparison.label}</strong>
                    </span>
                )}
            </div>

            {/* Metric cards — same set as super admin, scoped server-side. */}
            <div className="manager-dashboard__metrics grid grid-cols-1 tablet:grid-cols-3 desktop:grid-cols-5 gap-12px">
                <MetricCard value={counts.workOrders.total} label="Work Orders" />
                <MetricCard value={counts.inspections.total} label="Inspections" />
                <MetricCard
                    value={counts.completedCurrent}
                    label="Completed (this period)"
                    current={counts.completedCurrent}
                    previous={counts.completedPrevious}
                    deltaMode="absolute"
                />
                {revenue && (
                    <MetricCard
                        value={formatCurrencyCompact(revenue.current)}
                        label={activeScope === 'me' ? 'Money I made' : 'Company revenue'}
                        current={revenue.current}
                        previous={revenue.previous}
                        deltaMode="percent"
                    />
                )}
                {revenue && (
                    <MetricCard
                        value={formatCurrencyCompact(revenue.estimateApprovedTotal)}
                        label={`Approved estimates (${revenue.estimateApprovedCount})`}
                    />
                )}
            </div>

            {/* Status breakdowns — same primitive used by SuperAdminDashboard.
                Rows deep-link into the list pre-filtered to that status. */}
            <div className="grid grid-cols-1 tablet:grid-cols-2 gap-16px">
                <StatusBreakdown
                    title="Work Orders by Status"
                    statusMap={counts.workOrders.byStatus}
                    onSelectStatus={(status) => navigate(`/work-orders?status=${status}`)}
                />
                <StatusBreakdown
                    title="Inspections by Status"
                    statusMap={counts.inspections.byStatus}
                    onSelectStatus={(status) => navigate(`/inspections?status=${status}`)}
                />
            </div>

            {/* Inspection quality — how sites are scoring, and whether any
                critical finding is sitting open long enough to escalate.
                Tiles click through to the inspections list pre-filtered
                to that rating tier. */}
            <InspectionRatingsPanel
                counts={counts.inspections}
                onSelectRating={handleSelectRating}
                onSelectCritical={handleSelectCritical}
                className="mt-16px"
            />
        </div>
    );
};

export default ManagerDashboard;
